import { skipToken, type SkipToken } from "@reduxjs/toolkit/query";
import type {
  Company,
  CompanyPatchPayload,
  PlaybookRoutingGroupRule,
} from "@repo/app-types/entities/company";
import type {
  AdminApiAiCreditStatus,
  AdminApiAiUsage,
  AdminApiCreditUsage,
  AdminApiVoiceUsage,
  CreditType,
} from "@repo/app-types/entities/creditUsage";
import type {
  AdminApiContractProducts,
  AdminApiEntitlementUsage,
  AdminApiLicenseUtilization,
} from "@repo/app-types/entities/entitlement";
import { setCompany } from "../../features/company/companySlice.js";
import { buildListTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

export const companyApi = api.injectEndpoints({
  endpoints: (builder) => ({
    companyEntitlements: builder.query<
      AdminApiEntitlementUsage[],
      void,
      { data: { entitlements: AdminApiEntitlementUsage[] } }
    >({
      query: () => "/company/entitlements",
      transformResponse: (response) => response.data.entitlements,
    }),

    companyDomains: builder.query<string[], void, { domains: string[] }>({
      query: () => "/domains",
      transformResponse: (response) => response.domains,
      providesTags: [buildListTag(TagType.CompanyDomain, [])],
    }),

    updateCompanyDomains: builder.mutation<
      void,
      { domains: readonly string[] },
      { success: true }
    >({
      query: (body) => ({
        method: "POST",
        url: "/domains",
        body,
      }),
      transformResponse: () => undefined,
      // Optimistically apply the submitted domains so the settings form doesn't
      // flash the previous list while the refetch is in flight. Reconciled with
      // the server's canonical result by invalidatesTags.
      async onQueryStarted({ domains }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          companyApi.util.updateQueryData("companyDomains", undefined, () => [
            ...domains,
          ]),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [buildListTag(TagType.CompanyDomain, [])],
    }),

    creditUsage: builder.query<
      AdminApiCreditUsage,
      { creditType: CreditType; periodOffset: number },
      { data: { credit_usage: AdminApiCreditUsage } }
    >({
      query: ({ creditType, periodOffset }) =>
        `/company/credit-usage/${creditType}?period_offset=${periodOffset}`,
      transformResponse: (response) => response.data.credit_usage,
      providesTags: [buildListTag(TagType.CreditUsage, [])],
    }),

    aiCreditStatus: builder.query<
      AdminApiAiCreditStatus | null,
      void,
      { data: { ai_credit_status: AdminApiAiCreditStatus | null } }
    >({
      query: () => "/company/ai-credit-status",
      transformResponse: (response) => response.data.ai_credit_status,
      // Kept across page changes; each fetch reads Stripe and Metronome.
      keepUnusedDataFor: 300,
    }),

    licenseUtilization: builder.query<
      AdminApiLicenseUtilization | null,
      void,
      { data: { license_utilization: AdminApiLicenseUtilization | null } }
    >({
      query: () => "/company/license-utilization",
      transformResponse: (response) => response.data.license_utilization,
      providesTags: [buildListTag(TagType.LicenseUtilization, [])],
    }),

    contractProducts: builder.query<
      AdminApiContractProducts,
      void,
      { data: { contract_products: AdminApiContractProducts } }
    >({
      query: () => "/company/contract-products",
      transformResponse: (response) => response.data.contract_products,
    }),

    playbookRouting: builder.query<
      PlaybookRoutingGroupRule[],
      void,
      { data: { rules: PlaybookRoutingGroupRule[] } }
    >({
      query: () => "/company/playbook-routing",
      transformResponse: (response) => response.data.rules,
      providesTags: [buildListTag(TagType.PlaybookRoutingRules, [])],
    }),

    updatePlaybookRouting: builder.mutation<
      PlaybookRoutingGroupRule[],
      { rules: PlaybookRoutingGroupRule[] },
      { data: { rules: PlaybookRoutingGroupRule[] } }
    >({
      query: (body) => ({
        method: "PUT",
        url: "/company/playbook-routing",
        body,
      }),
      transformResponse: (response) => response.data.rules,
      // Optimistically apply the submitted rules so reorders (and create/update/
      // delete) render instantly instead of waiting for the round-trip + refetch.
      // Reconciled with the server's canonical result by invalidatesTags.
      async onQueryStarted({ rules }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          companyApi.util.updateQueryData(
            "playbookRouting",
            undefined,
            () => rules,
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [buildListTag(TagType.PlaybookRoutingRules, [])],
    }),

    updateCompany: builder.mutation<
      Company,
      CompanyPatchPayload,
      { data: { company: Company } }
    >({
      query: (body) => ({
        method: "PATCH",
        url: "/company",
        body,
      }),
      transformResponse: (response) => response.data.company,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setCompany(data));
        } catch {
          // Error handling is done by the caller
        }
      },
    }),
  }),
});

export const {
  useAiCreditStatusQuery,
  useCompanyDomainsQuery,
  useCompanyEntitlementsQuery,
  useContractProductsQuery,
  useCreditUsageQuery,
  useLicenseUtilizationQuery,
  usePlaybookRoutingQuery,
  useUpdatePlaybookRoutingMutation,
  useUpdateCompanyMutation,
  useUpdateCompanyDomainsMutation,
} = companyApi;

/**
 * One credit type's view of the shared credit-usage query, narrowed to that
 * type's response shape so sections need no discriminant checks.
 */
function useCreditUsageOfTypeQuery<T extends AdminApiCreditUsage>(
  creditType: T["credit_type"],
  args: { periodOffset: number } | SkipToken,
) {
  return useCreditUsageQuery(
    args === skipToken
      ? skipToken
      : { creditType, periodOffset: args.periodOffset },
    {
      selectFromResult: (result) => ({
        ...result,
        data: narrowCreditUsage<T>(result.data, creditType),
        currentData: narrowCreditUsage<T>(result.currentData, creditType),
      }),
    },
  );
}

function narrowCreditUsage<T extends AdminApiCreditUsage>(
  usage: AdminApiCreditUsage | undefined,
  creditType: T["credit_type"],
): T | undefined {
  return usage?.credit_type === creditType ? (usage as T) : undefined;
}

export const useAiUsageQuery = (args: { periodOffset: number } | SkipToken) =>
  useCreditUsageOfTypeQuery<AdminApiAiUsage>("ai_credits", args);

export const useVoiceUsageQuery = (
  args: { periodOffset: number } | SkipToken,
) => useCreditUsageOfTypeQuery<AdminApiVoiceUsage>("voice_credits", args);
